import { useCallback, useRef, useState } from "react";
import Drawer from "../../../../../../../../components/SideDrawer/SideDrawer";
import Button from "../../../../../../../../components/Button/Button";
import CloseIcon from "../../../../../../../../assets/svg/close-icon";
import AddIcon from "../../../../../../../../assets/svg/add-icon";
import DocumentAddedIcon from "../../../../../../../../assets/svg/document-added-icon";
import CompletedIcon from "../../../../../../../../assets/svg/completed-icon";
import AlertIcon from "../../../../../../../../assets/svg/alert-icon";
import ClipLoader from "../../../../../../../../components/Common/ClipLoader";
import { Tooltip } from "../../../../../../../../components/Tooltip/Tooltip";
import { useUploadFilesMutation } from "../../../../../../../../redux/services/kbFilesApi";
import { customToast } from "../../../../../../../../utils/custom-toast/custom-toast";
import { getFileTypeIcon } from "../../../../../../../../utils/functions/file-icon-extractor";
import {
  KB_FILES_LIMITS,
  ACCEPT_STRING,
  SUPPORTED_FORMATS_LABEL,
  MAX_FILE_SIZE_LABEL,
  UPLOAD_MESSAGES,
} from "../constants/fileManagement.constants";
import { validateFile, formatFileSize } from "../utils/fileManagement.utils";
import type {
  UploadStatus,
  UploadFilesDrawerProps,
} from "../types/fileManagement.types";

const UploadFilesDrawer = ({
  open,
  onClose,
  parentFolderId,
}: UploadFilesDrawerProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [failedFiles, setFailedFiles] = useState<string[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const [uploadFiles] = useUploadFilesMutation();

  const addFiles = useCallback((incoming: FileList | File[]) => {
    setFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const validFiles: File[] = [];

      Array.from(incoming).forEach((file) => {
        if (existingNames.has(file.name)) return;

        const error = validateFile(file);
        if (error) {
          customToast(error, "error", 4000, undefined, "bottom-left");
          return;
        }

        validFiles.push(file);
        existingNames.add(file.name);
      });

      if (validFiles.length === 0) return prev;

      const remaining = KB_FILES_LIMITS.MAX_UPLOAD_BATCH - prev.length;
      if (remaining <= 0) {
        customToast(
          `Maximum ${KB_FILES_LIMITS.MAX_UPLOAD_BATCH} files per upload.`,
          "error",
          4000,
          undefined,
          "bottom-left"
        );
        return prev;
      }

      const toAdd = validFiles.slice(0, remaining);
      if (toAdd.length < validFiles.length) {
        customToast(
          `Only ${toAdd.length} of ${validFiles.length} files added (limit ${KB_FILES_LIMITS.MAX_UPLOAD_BATCH}).`,
          "error",
          4000,
          undefined,
          "bottom-left"
        );
      }

      return [...prev, ...toAdd];
    });
  }, []);

  const removeFile = useCallback((fileName: string) => {
    setFiles((prev) => prev.filter((f) => f.name !== fileName));
  }, []);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === dropZoneRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleBrowseClick = () => {
    if (isDone) {
      setFiles([]);
      setUploadStatus("idle");
      setFailedFiles([]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
    }
  };

  const handleUpload = useCallback(async () => {
    if (files.length === 0) return;

    setUploadStatus("uploading");
    setFailedFiles([]);

    try {
      const formData = new FormData();
      formData.append("parent_folder_id", parentFolderId || "");
      files.forEach((file) => formData.append("files", file));

      const response = await uploadFiles(formData).unwrap();

      if (response.failed && response.failed.length > 0) {
        const failedNames = response.failed.map((f) => f.name);
        setFailedFiles(failedNames);

        if (response.uploaded.length > 0) {
          customToast(
            `${response.uploaded.length} file(s) uploaded, ${response.failed.length} failed.`,
            "error"
          );
        } else {
          customToast(UPLOAD_MESSAGES.ERROR_ALL_FAILED, "error");
        }
        setUploadStatus("error");
      } else {
        customToast(
          `${response.uploaded.length} file(s) uploaded successfully.`,
          "success"
        );
        setUploadStatus("success");
      }
    } catch {
      setFailedFiles(files.map((f) => f.name));
      customToast(UPLOAD_MESSAGES.ERROR_UPLOAD_FAILED, "error");
      setUploadStatus("error");
    }
  }, [files, parentFolderId, uploadFiles]);

  const resetAndClose = useCallback(() => {
    setFiles([]);
    setUploadStatus("idle");
    setFailedFiles([]);
    onClose();
  }, [onClose]);

  const isUploading = uploadStatus === "uploading";
  const isDone = uploadStatus === "success" || uploadStatus === "error";

  return (
    <Drawer
      open={open}
      onClose={isUploading ? () => {} : resetAndClose}
      direction="right"
      size="520px"
      overlayColor="rgba(0,0,0,0.7)"
      duration={300}
      closeOnOverlayClick={!isUploading}
      closeOnEscape={!isUploading}
      drawerContainerClassName="rounded-l-[8px]"
    >
      <div className="p-5 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between shrink-0">
          <DocumentAddedIcon className="w-10 h-10 text-verba-blue dark:text-verba-blue-dk" />
          <button
            type="button"
            className="w-4 h-4 bg-tag-gray dark:bg-tag-gray-dk rounded-full flex items-center justify-center hover:scale-110 transition-transform"
            aria-label="Close upload drawer"
            onClick={resetAndClose}
            disabled={isUploading}
          >
            <CloseIcon className="w-1.5 h-1.5 text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>

        {/* Title & Description */}
        <div className="mt-3 shrink-0">
          <h2 className="text-2xl font-medium text-verba-indigo dark:text-verba-indigo-dk">
            {UPLOAD_MESSAGES.TITLE}
          </h2>
          <p className="text-base text-verba-indigo dark:text-verba-indigo-dk mt-1">
            Files in{" "}
            <span className="font-semibold">{SUPPORTED_FORMATS_LABEL}</span>{" "}
            formats are supported.
          </p>
        </div>

        {/* Drop Zone */}
        <div
          ref={dropZoneRef}
          onClick={handleBrowseClick}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`mt-4 border group bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-verba-blue-150 dark:border-verba-blue-150-dk border-dashed  hover:bg-card-highlight dark:hover:bg-card-highlight-dk rounded-lg py-6 px-4 cursor-pointer shrink-0 ${
            isDragging
              ? "border-verba-blue-150 dark:border-verba-blue-150-dk border-dashed bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
              : ""
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileInputChange}
            accept={ACCEPT_STRING}
            multiple
            disabled={files.length >= KB_FILES_LIMITS.MAX_UPLOAD_BATCH}
          />
          <div className="flex items-center gap-2">
            <AddIcon className="w-8 h-8 text-verba-blue dark:text-verba-blue-dk group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk" />
            <div className="flex flex-col items-start">
              <span className="text-base font-medium text-verba-indigo dark:text-verba-indigo-dk">
                Browse Files
              </span>
              <p className="text-sm text-dark-gray dark:text-dark-gray-dk flex gap-1">
                Select files or Drag &amp; Drop here
              </p>
            </div>
          </div>
        </div>

        {/* Limits Info */}
        <div className="flex items-center gap-1 mt-3 text-xs shrink-0">
          <span className="text-dark-gray dark:text-dark-gray-dk">
            {UPLOAD_MESSAGES.MAX_FILE_SIZE_PREFIX}
          </span>
          <span className="font-medium text-heading-text dark:text-heading-text-dk">
            {MAX_FILE_SIZE_LABEL}
          </span>
          <div className="w-px h-3 bg-border-gray dark:bg-border-gray-dk mx-1" />
          <span className="text-dark-gray dark:text-dark-gray-dk">
            {UPLOAD_MESSAGES.MAX_FILES_PREFIX}
          </span>
          <span className="font-medium text-heading-text dark:text-heading-text-dk">
            {KB_FILES_LIMITS.MAX_UPLOAD_BATCH}
          </span>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="my-3 min-h-0 flex flex-col gap-3 p-4 border border-dashed border-verba-blue-150 dark:border-verba-blue-150-dk bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk rounded-lg">
            <div className="flex items-center justify-between border-b border-border-gray dark:border-border-gray-dk pb-2 shrink-0">
              <p className="text-base font-medium text-verba-indigo dark:text-verba-indigo-dk">
                {`Selected ${files.length} ${files.length === 1 ? "File" : "Files"}`}
              </p>
            </div>

            {/* Table Header */}
            <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center shrink-0 pr-2">
              <span className="text-sm font-semibold text-gray-placeholder dark:text-gray-placeholder-dk">
                {UPLOAD_MESSAGES.FILE_NAME_HEADER}
              </span>
              <span className="text-sm font-semibold text-gray-placeholder dark:text-gray-placeholder-dk">
                {UPLOAD_MESSAGES.FILE_SIZE_HEADER}
              </span>
              <span className="w-3.5" />
            </div>

            {/* Scrollable file rows */}
            <div className="min-h-0 max-h-full flex flex-col gap-3 overflow-y-auto pr-3.5">
              {files.map((fileItem) => {
                const isFailed = failedFiles.includes(fileItem.name);
                return (
                  <div
                    key={fileItem.name}
                    className="grid grid-cols-[1fr_auto_auto] gap-2 items-center"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <img
                        src={getFileTypeIcon(fileItem.name)}
                        alt=""
                        className="w-6 h-6 shrink-0"
                      />
                      <Tooltip content={fileItem.name}>
                        <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk font-medium truncate min-w-0">
                          {fileItem.name}
                        </span>
                      </Tooltip>
                    </div>
                    <span className="text-xs text-dark-gray dark:text-dark-gray-dk whitespace-nowrap">
                      {formatFileSize(fileItem.size)}
                    </span>

                    {/* Status indicators */}
                    {uploadStatus === "idle" && (
                      <button
                        type="button"
                        className="w-3.5 h-3.5 bg-verba-indigo dark:bg-verba-indigo-dk rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(fileItem.name);
                        }}
                      >
                        <CloseIcon className="w-[5px] h-[5px] text-pure-white dark:text-pure-white-dk" />
                      </button>
                    )}
                    {isUploading && (
                      <ClipLoader
                        className="text-verba-blue dark:text-verba-blue-dk"
                        thickness={2}
                        size={14}
                      />
                    )}
                    {uploadStatus === "success" && (
                      <CompletedIcon className="w-4 h-4 text-verba-green-success dark:text-verba-green-success-dk" />
                    )}
                    {uploadStatus === "error" && isFailed && (
                      <AlertIcon className="w-4 h-4 text-verba-red-alert dark:text-verba-red-alert-dk" />
                    )}
                    {uploadStatus === "error" &&
                      !isFailed &&
                      failedFiles.length < files.length && (
                        <CompletedIcon className="w-4 h-4 text-verba-green-success dark:text-verba-green-success-dk" />
                      )}
                  </div>
                );
              })}
            </div>

            {/* Upload status messages */}
            {isUploading && (
              <div className="flex items-center gap-2 mt-2 shrink-0">
                <ClipLoader
                  className="text-verba-blue dark:text-verba-blue-dk"
                  thickness={2}
                  size={14}
                />
                <span className="text-xs text-verba-blue dark:text-verba-blue-dk">
                  {`Uploading ${files.length} file${files.length !== 1 ? "s" : ""}…`}
                </span>
              </div>
            )}
            {uploadStatus === "success" && (
              <div className="flex items-center gap-1 mt-2 shrink-0">
                <CompletedIcon className="w-4 h-4 text-verba-green-success dark:text-verba-green-success-dk" />
                <span className="text-xs font-medium text-verba-green-success dark:text-verba-green-success-dk">
                  {UPLOAD_MESSAGES.SUCCESS_ALL}
                </span>
              </div>
            )}
            {uploadStatus === "error" && (
              <div className="flex items-center gap-1 mt-2 shrink-0">
                <AlertIcon className="w-3.5 h-3.5 text-verba-red-alert dark:text-verba-red-alert-dk" />
                <span className="text-xs font-medium text-verba-red-alert dark:text-verba-red-alert-dk">
                  {failedFiles.length > 0
                    ? `${failedFiles.length} file(s) failed to upload`
                    : UPLOAD_MESSAGES.ERROR_UPLOAD_FAILED}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            {uploadStatus === "idle" && (
              <div className="flex items-center justify-end gap-2 mt-3 shrink-0">
                <Button
                  type="button"
                  innerText={UPLOAD_MESSAGES.CANCEL_BUTTON}
                  onClick={() => setFiles([])}
                  buttonContainerClass="py-1 px-6 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                  innerTextClass="text-sm font-medium text-heading-text dark:text-heading-text-dk whitespace-nowrap"
                />
                <Button
                  type="button"
                  innerText={`Upload (${files.length})`}
                  onClick={handleUpload}
                  disabled={files.length === 0}
                  buttonContainerClass="py-1 px-6 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk disabled:opacity-50 disabled:cursor-not-allowed"
                  innerTextClass="text-sm font-medium text-pure-white dark:text-pure-white-dk whitespace-nowrap"
                />
              </div>
            )}
            {isDone && (
              <div className="flex items-center justify-end gap-2 mt-3 shrink-0">
                <Button
                  type="button"
                  innerText={UPLOAD_MESSAGES.CLOSE_BUTTON}
                  onClick={resetAndClose}
                  buttonContainerClass="py-1 px-6 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk"
                  innerTextClass="text-sm font-medium text-pure-white dark:text-pure-white-dk whitespace-nowrap"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default UploadFilesDrawer;
